import { DangerAlert } from "components/common/Alerts";
import { Breadcrumb, BreadcrumbLink } from "components/common/Breadcrumb";
import ContentLoading from "components/common/ContentLoading";
import Disclaimer from "interfaces/Disclaimer";
import FOB from "interfaces/FOB";
import Quote from "interfaces/Quote";
import QuoteExpiration from "interfaces/QuoteExpiration";
import UserIdAndName from "interfaces/UserIdAndName";
import * as moment from "moment";
import * as React from "react";
import { RouteComponentProps } from "react-router";
import apifetch from "utils/apifetch";
import extractErrorMessage from "utils/extractErrorMessage";
import Step1 from "./Step1";
import Step2 from "./Step2";
import Step3 from "./Step3";
import Step4 from "./Step4";

interface State {
    isLoading: boolean;
    hasValidationErrors: boolean;
    errorMessage?: string;
    stepNumber: number;
    data: Quote;
    salespeople: UserIdAndName[];
    defaultSalesTaxRate: number;
    disclaimers: Disclaimer[];
    fobs: FOB[];
    quoteExpirations: QuoteExpiration[];
}

export default class Add extends React.Component<
    RouteComponentProps<{}>,
    State
> {
    private baseUrl: string;

    constructor(props: RouteComponentProps<{}>) {
        super(props);

        this.state = {
            isLoading: true,
            hasValidationErrors: false,
            errorMessage: undefined,
            stepNumber: 1,
            data: {
                id: 0,
                jobId: 0,
                jobName: "",
                date: "",
                expirationDate: "",
                isMaster: false,
                isCashSale: false,
                purchaseOrderNumber: "",
                preparerName: "",
                quoteStatusId: 0,
                fobId: 1,
                price: 0,
                isDraft: false,
                customers: [],
                productGroups: [],
                disclaimers: [],
                quoteHistories: [],
                quoteOrders: [],
                salesTaxRate: 0
                //hasProducts: false
            },
            salespeople: [],
            defaultSalesTaxRate: 0,
            disclaimers: [],
            fobs: [],
            quoteExpirations: [],
        };

        this.baseUrl = document
            .getElementsByTagName("base")[0]
            .getAttribute("href")!;

        this.handleStep1Submit = this.handleStep1Submit.bind(this);
        this.handleStep2Submit = this.handleStep2Submit.bind(this);
        this.handleStep3Submit = this.handleStep3Submit.bind(this);
        this.handleStep4Submit = this.handleStep4Submit.bind(this);

        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleCancel = this.handleCancel.bind(this);
    }
    public render() {
        if (this.state.isLoading) {
            return (
                <div className="container">
                    <ContentLoading topOffset={350} />
                </div>
            );
        }

        let step = null;
        switch (this.state.stepNumber) {
            case 1:
                step = (
                    <Step1
                        onSubmit={this.handleStep1Submit}
                        onCancel={this.handleCancel}
                        isNewQuote={true}
                        isDraft={false}
                        salespeople={this.state.salespeople}
                        defaultSalesTaxRate={this.state.defaultSalesTaxRate}
                        fobs={this.state.fobs}
                        quoteExpirations={this.state.quoteExpirations}
                        data={this.state.data}
                    />
                );
                break;
            case 2:
                step = (
                    <Step2
                        onSubmit={this.handleStep2Submit}
                        onCancel={this.handleCancel}
                        isNewQuote={true}
                        isDraft={false}
                        isMasterQuote={this.state.data.isMaster}
                        customers={this.state.data.customers}
                    />
                );
                break;
            case 3:
                step = (
                    <Step3
                        onSubmit={this.handleStep3Submit}
                        onCancel={this.handleCancel}
                        isNewQuote={true}
                        isDraft={false}
                        salesTaxRate={this.state.data.salesTaxRate}
                        productGroups={this.state.data.productGroups}
                    />
                );
                break;
            case 4:
                step = (
                    <Step4
                        onSubmit={this.handleStep4Submit}
                        onCancel={this.handleCancel}
                        isNewQuote={true}
                        isDraft={false}
                        disclaimers={this.state.disclaimers}
                        selectedDisclaimers={this.state.data.disclaimers}
                        data={this.state.data}
                    />
                );
                break;
        }

        return (
            <div className="container">
                <div className="animated fadeIn">
                    <Breadcrumb>
                        <BreadcrumbLink to="/">Home</BreadcrumbLink>
                        <BreadcrumbLink to="/quotes">Quotes</BreadcrumbLink>
                        <BreadcrumbLink isActive={true}>Add New Quote</BreadcrumbLink>
                    </Breadcrumb>
                    {this.state.hasValidationErrors && (
                        <DangerAlert name="dangerAlert">
                            {this.state.errorMessage || "An error occurred."}
                        </DangerAlert>
                    )}
                    {step}
                </div>
            </div>
        );
    }

    public async componentDidMount() {
        if (this.state.isLoading) {
            let response = await apifetch("quotes/add", undefined, true);
            if (response.ok) {
                let data = (await response.json()) as {
                    salespeople: UserIdAndName[];
                    defaultSalesTaxRate: number;
                    disclaimers: Disclaimer[];
                    fobs: FOB[];
                    quoteExpirations: QuoteExpiration[];
                };

                //To Order Disclaimers in Ascending order by Display Order
                let sortedDisclaimers = data.disclaimers;
                sortedDisclaimers.sort((a, b) => a.displayOrder - b.displayOrder);

                // Set the default disclaimers
                // We do this here so they get added if you save the quote before the final step
                let stateData = this.state.data;

                stateData.salesTaxRate = data.defaultSalesTaxRate; //To make Taxable Yes by default

                for (let disclaimer of sortedDisclaimers) {
                    if (disclaimer.isDefault) {
                        stateData.disclaimers.push({
                            disclaimerId: disclaimer.id,
                            disclaimerText: disclaimer.text,
                        });
                    }
                }
                this.setState({
                    isLoading: false,
                    salespeople: data.salespeople,
                    defaultSalesTaxRate: data.defaultSalesTaxRate,
                    disclaimers: sortedDisclaimers,
                    fobs: data.fobs,
                    quoteExpirations: data.quoteExpirations
                });

                document.title = "Add New Quote - " + SITE_NAME;
            }
        }
    }

    private handleStep1Submit(data: any) {
        let stateData = this.state.data;

        stateData.date = data.date.toString();
        stateData.expirationDate = data.expirationDate.toString();
        stateData.isMaster = data.isMaster === "true";
        stateData.salespersonUserId = data.salespersonUserId;
        stateData.isCashSale = data.isCashSale === "true";
        stateData.purchaseOrderNumber = data.purchaseOrderNumber;
        stateData.quoteStatusId = parseInt(data.quoteStatusId, 10) || 0;
        stateData.jobId = data.jobId;
        stateData.jobName = data.jobName;
        stateData.fobId = data.fobId;
        stateData.quoteExpirationId = data.quoteExpirationId;
        stateData.fobOther = data.fobOther;

        if (data.isTaxable === "true") {
            stateData.salesTaxRate = (parseFloat(data.salesTaxRate) || 0) / 100;
        } else {
            stateData.salesTaxRate = undefined;
        }

        if (data.saveAsDraft) {
            stateData.isDraft = true;

            this.setState({
                data: stateData,
            });

            this.handleSubmit();
        } else {
            this.setState({
                data: stateData,
                stepNumber: 2,
            });
        }
    }

    private handleStep2Submit(data: any) {
        let stateData = this.state.data;

        //To Auto Update Disclaimer with Expiration
        let duration = moment
            .duration(moment(stateData.expirationDate).diff(moment(stateData.date)))
            .asDays();
        let allDisclaimers = this.state.disclaimers;

        function InsertDisclaimer(disclaimer: Disclaimer) {
            stateData.disclaimers.some((item) => disclaimer.id === item.disclaimerId)
                ? null
                : stateData.disclaimers.push({
                    disclaimerId: disclaimer.id,
                    disclaimerText: disclaimer.text,
                });
        }

        for (let disclaimer of allDisclaimers) {
            stateData.disclaimers.some((item) => disclaimer.id === item.disclaimerId)
                ? null
                : disclaimer.id == 2 && duration == 30
                    ? InsertDisclaimer(disclaimer)
                    : disclaimer.id == 4 && duration == 5
                        ? InsertDisclaimer(disclaimer)
                        : disclaimer.id == 5 && duration == 10
                            ? InsertDisclaimer(disclaimer)
                            : disclaimer.id == 6 && duration == 15
                                ? InsertDisclaimer(disclaimer)
                                : null;
        }

        stateData.customers = data.customers;

        if (data.saveAsDraft) {
            stateData.isDraft = true;

            this.setState({
                data: stateData,
            });

            this.handleSubmit();
        } else {
            this.setState({
                data: stateData,
                stepNumber: data.nextStepNumber,
            });
        }
    }

    private handleStep3Submit(data: any) {
        let stateData = this.state.data;
        stateData.productGroups = data.categories;

        if (data.saveAsDraft) {
            stateData.isDraft = true;

            this.setState({
                data: stateData,
            });

            this.handleSubmit();
        } else {
            this.setState({
                data: stateData,
                stepNumber: data.nextStepNumber,
            });
        }
    }

    private handleStep4Submit(data: any) {
        let stateData = this.state.data;
        stateData.disclaimers = data.disclaimers;

        if (data.nextStepNumber !== undefined) {
            this.setState({
                stepNumber: data.nextStepNumber,
            });
        } else {
            if (data.saveAsDraft) {
                stateData.isDraft = true;

                this.setState({
                    data: stateData,
                });
            }

            this.handleSubmit();
        }
    }

    private async handleSubmit() {
        let stateData = this.state.data;
        this.setState({ isLoading: true, errorMessage: undefined });
        if (stateData.id !== 0) {
            // Update the quote
            let response = await apifetch(
                "quotes/" + this.state.data.id,
                {
                    headers: new Headers({
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    }),
                    method: "PUT",
                    body: JSON.stringify(stateData),
                },
                true
            );
            if (response.ok) {
                this.setState({ isLoading: false });
                this.props.history.push("/quotes/" + this.state.data.id);
            } else {
                const errorText = await extractErrorMessage(response);
                this.setState({
                    hasValidationErrors: true,
                    errorMessage: errorText,
                    isLoading: false,
                });
            }
        } else {
            // Save as a new quote
            let response = await apifetch(
                "quotes",
                {
                    headers: new Headers({
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    }),
                    method: "POST",
                    body: JSON.stringify(stateData),
                },
                true
            );
            if (response.ok) {
                let responseData = await response.json();
                this.setState({ isLoading: false });
                this.props.history.push("/quotes/" + responseData.id);
            } else {
                const errorText = await extractErrorMessage(response);
                this.setState({
                    hasValidationErrors: true,
                    errorMessage: errorText,
                    isLoading: false,
                });
            }
        }
    }

    private handleCancel() {
        this.props.history.push("/quotes");
    }
}



// WEBPACK FOOTER //
// ./ClientApp/components/quotes/Add.tsx